import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import { resetAll } from "../actions/resetAll";

export type UserState = {
  name: string;
  uuid?: string;
  pan: string;
  email: string;
  mobile: string;
  isKycCompleted?: boolean;
  guid: string;
  otpToken: string;
  isNewUser?: boolean;
  token: string | null;
  temporaryToken: string | null;
  transactionToken: string;
  deviceId: string | null;
  mpin: string | null;
  theme: "light" | "dark";
  isBiometricsEnrolled: boolean;
  guestToken: string;
  isLoggedIn: boolean;
};

const initialState: UserState = {
  name: "",
  uuid: "",
  guid: "",
  pan: "",
  isKycCompleted: false,
  isNewUser: false,
  token: "", // required
  otpToken: "", //required
  temporaryToken: "", //required
  transactionToken: "",
  deviceId: "",
  mpin: "",
  theme: "light",
  isBiometricsEnrolled: false,
  email: "",
  mobile: "",
  guestToken: "",
  isLoggedIn: false,
};

const userSlice = createSlice({
  name: "user",
  initialState: initialState,
  reducers: {
    setUserDetails: (state, action: PayloadAction<Partial<UserState>>) => {
      Object.assign(state, action.payload);
    },
    setPan: (state, action: PayloadAction<string>) => {
      state.pan = action.payload;
    },
    setEmail: (state, action: PayloadAction<string>) => {
      state.email = action.payload;
    },
    setMobile: (state, action: PayloadAction<string>) => {
      state.mobile = action.payload;
    },
    setUuid: (state, action: PayloadAction<string>) => {
      state.uuid = action.payload;
    },
    setKyc: (state, action: PayloadAction<boolean>) => {
      state.isKycCompleted = action.payload;
    },
    setIsNewUser: (state, action: PayloadAction<boolean>) => {
      state.isNewUser = action.payload;
    },
    setToken: (state, action: PayloadAction<string>) => {
      Object.assign(state, { token: action.payload });
    },
    setTemporaryToken: (state, action: PayloadAction<string>) => {
      Object.assign(state, { temporaryToken: action.payload });
    },
    setOtpToken: (state, action: PayloadAction<string>) => {
      Object.assign(state, { otpToken: action.payload });
    },
    setGuestToken: (state, action: PayloadAction<string>) => {
      state.guestToken = action.payload;
    },
    setIsBiometricsEnrolled: (state, action: PayloadAction<boolean>) => {
      Object.assign(state, { isBiometricsEnrolled: action.payload });
    },
    setTheme: (state, action: PayloadAction<"light" | "dark">) => {
      Object.assign(state, { theme: action.payload });
    },
    setIsLoggedIn: (state, action: PayloadAction<boolean>) => {
      state.isLoggedIn = action.payload;
    },
    logout: (state) => {
      Object.assign(state, initialState);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetAll, () => initialState);
    builder.addCase("persist/REHYDRATE", (state, action: any) => {
      // Force isLoggedIn to false on every app launch/rehydration
      if (action.payload && action.payload.user) {
        return {
          ...action.payload.user,
          isLoggedIn: false,
        };
      }
      return state;
    });
  },
});

export const { reducer: userReducer, actions: userActions } = userSlice;

export default userSlice;
